import { Box, Button, Dialog, DialogContent, TextField, Typography, styled } from '@mui/material';

const GRID_COLUMNS = '40px 272px 1fr';

const getGridColumns = (columnsCount: number, hasRadio: boolean) => {
  const radioColumn = hasRadio ? '40px ' : '';
  // minmax(0, …): a bare 1fr never shrinks below its content, so a long name would widen the
  // column past the dialog instead of ellipsizing.
  const dataColumns = Array(columnsCount).fill('minmax(0, 1fr)').join(' ');
  return `${radioColumn}${dataColumns}`;
};

export const StyledDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'dialogHeight',
})<{ dialogHeight?: string | number }>(({ dialogHeight }) => ({
  '& .MuiDialog-paper': {
    width: 550,
    height: dialogHeight || '80vh',
    borderRadius: '1rem',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    boxShadow: '0px 1px 8px 0px rgba(0,0,0,0.15)',
  },
}));

export const StyledDialogContent = styled(DialogContent, {
  shouldForwardProp: (prop) => prop !== 'dialogHeight',
})<{ dialogHeight?: string | number }>(({ dialogHeight }) => ({
  gap: '1rem',
  padding: 0,
  minHeight: '30vh',
  ...(dialogHeight ? { flex: 1 } : { maxHeight: '50vh' }),
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

export const SearchField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '0.625rem',
    border: 'none',
    backgroundColor: '#f5f5f7',
    fontSize: 12,
    lineHeight: 1.6,
    padding: '0.5rem 0.75rem !important',
    width: '100%',

    '& fieldset': {
      border: 'none',
      padding: '0 !important',
    },

    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: 'none' },
    '& input': {
      padding: 0,
      '&::placeholder': {
        color: '#807d93',
        opacity: 1,
      },
    },
  },
}));

export const TableContainer = styled(Box)(() => ({
  flex: 1,
  overflow: 'hidden',
  borderRadius: '0.5rem',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
}));

export const ListContainer = styled(Box)(() => ({
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
}));

interface GridProps {
  columnsCount?: number;
  hasRadioButton?: boolean;
}

export const HeaderRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'columnsCount' && prop !== 'hasRadioButton',
})<GridProps>(({ theme, columnsCount = 1, hasRadioButton = true }) => ({
  display: 'grid',
  gridTemplateColumns:
    columnsCount === 1 && hasRadioButton
      ? GRID_COLUMNS
      : getGridColumns(columnsCount, hasRadioButton),
  backgroundColor: theme.palette.accentExtension.header,
  flexShrink: 0,
  height: 45,
  position: 'sticky',
  top: 0,
  zIndex: 1,
}));

export const HeaderCell = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  padding: '0 0.5rem',
  height: 45,
}));

export const HeaderText = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  color: theme.palette.text.secondary,
}));

interface UserRowProps {
  isEven: boolean;
  columnsCount?: number;
  hasRadioButton?: boolean;
}

export const UserRow = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isEven' && prop !== 'columnsCount' && prop !== 'hasRadioButton',
})<UserRowProps>(({ theme, isEven, columnsCount = 1, hasRadioButton = true }) => ({
  display: 'grid',
  gridTemplateColumns:
    columnsCount === 1 && hasRadioButton
      ? GRID_COLUMNS
      : getGridColumns(columnsCount, hasRadioButton),
  cursor: 'pointer',
  backgroundColor: isEven ? theme.palette.grey[50] : theme.palette.background.paper,
  '&:hover': { backgroundColor: theme.palette.action.hover },
  alignItems: 'center',
  minHeight: 45,
}));

export const RadioCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 45,

  '& .MuiRadio-root': {
    padding: 0,
    '& .MuiSvgIcon-root': {
      display: 'none',
    },
    '&::before': {
      content: '""',
      width: 16,
      height: 16,
      borderRadius: '50%',
      border: `1px solid ${theme.palette.grey[300]}`,
      backgroundColor: 'rgba(255,255,255,0.9)',
      boxSizing: 'border-box',
    },
    '&.Mui-checked::before': {
      border: `1px solid ${theme.palette.accent.main}`,
    },
    '&.Mui-checked::after': {
      content: '""',
      position: 'absolute',
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: theme.palette.accent.main,
    },
  },
}));

export const UserCell = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '1.5rem',
  padding: '0 0.5rem',
  height: 45,
}));

export const DataCell = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  padding: '0 0.5rem',
  height: 45,
}));

export const CellText = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  color: theme.palette.grey[800],
}));

export const ActionsContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '1rem',
  flex: 1,
}));

export const CancelButton = styled(Button)(({ theme }) => ({
  flex: 1,
  borderRadius: '0.625rem',
  border: `1px solid ${theme.palette.accent.main}`,
  color: theme.palette.accent.main,
  fontSize: 12,
  textTransform: 'none',
  padding: '0.375rem 1rem',
  '&:hover': {
    border: `1px solid ${theme.palette.accent.main}`,
    backgroundColor: `${theme.palette.accent.main}14`,
  },
}));

export const ConfirmButton = styled(Button)(({ theme }) => ({
  flex: 1,
  borderRadius: '0.625rem',
  backgroundColor: theme.palette.accent.main,
  color: '#fff',
  fontSize: 12,
  textTransform: 'none',
  padding: '0.375rem 1rem',
  '&:hover': {
    backgroundColor: theme.palette.accent.dark,
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.accent.main,
    color: theme.palette.accent.main,
  },
}));

export const EditIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="1.05rem"
    height="1.05rem"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M13.2006 12.5346C13.4767 12.5346 13.7006 12.7585 13.7006 13.0346C13.7005 13.3107 13.4767 13.5346 13.2006 13.5346H9.20058C8.92461 13.5345 8.7007 13.3106 8.70058 13.0346C8.70058 12.7585 8.92454 12.5347 9.20058 12.5346H13.2006ZM9.7696 2.84646C10.277 2.33892 11.0997 2.33869 11.6075 2.84581L12.9877 4.22472C13.4957 4.73217 13.4965 5.55532 12.989 6.06326L6.47272 12.5828C6.29168 12.7639 6.06096 12.8877 5.80996 12.9383L2.8998 13.5248C2.73572 13.5579 2.56571 13.5064 2.44733 13.3881C2.32901 13.2697 2.27756 13.0997 2.31061 12.9357L2.8985 10.0281C2.94915 9.77755 3.07262 9.54745 3.25332 9.36665L9.7696 2.84646ZM10.9011 3.55349C10.7839 3.43643 10.5937 3.43633 10.4766 3.55349L3.96035 10.0737C3.91871 10.1154 3.89002 10.1683 3.87832 10.226L3.43952 12.3953L5.61269 11.9578C5.67064 11.9461 5.7239 11.9176 5.76569 11.8758L12.2813 5.35623C12.3985 5.23901 12.3986 5.04886 12.2813 4.93175L10.9011 3.55349Z"
      fill="#504E5A"
    />
  </svg>
);
